// HotelFields — Presentational field group for the "hotel" event type. Renders
// the controlled inputs the source's `renderHotelForm()` produced: Hotel Name,
// Address, Check-in/Check-out Date, and the optional Confirmation Number,
// including the per-field required-error display.
//
// Extracted verbatim from `AddEventForm.js` (`renderHotelForm`) as part of the
// behavior-preserving refactor (issue #240). The inputs are fully controlled by
// props: values come from `formData`, edits call `onChange(field, value)` (the
// source's `handleInputChange`), and per-field errors come from `errors` (the
// source's `validationErrors`). The two render helpers — `renderFieldError` and
// `getFieldBorderStyle` — are copied verbatim from the source and read only the
// `errors` prop, so the component stays render-only with NO internal state.
// Labels, placeholders, field order, the `validation-error-<field>` test ids,
// styles, and attributes are identical to the source.
//
// Props:
//   formData - object; the controlled field values
//   errors   - object; per-field validation error messages (keyed by field)
//   onChange - (field, value) => void invoked on each input change
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';

export const HotelFields = React.memo(function HotelFields({
  formData,
  errors,
  onChange
}) {
  // Native date inputs have a browser-enforced minimum rendered width
  // (~150px) that two side-by-side columns can't fit on a phone screen —
  // stack to one column below the mobile breakpoint instead of letting the
  // second field overflow the modal.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const renderFieldError = (field) => {
    if (!errors[field]) return null;
    return (
      <span
        data-testid={`validation-error-${field}`}
        style={{
          display: 'block',
          fontSize: '12px',
          color: '#dc2626',
          marginTop: '4px'
        }}
      >
        {errors[field]}
      </span>
    );
  };

  const getFieldBorderStyle = (field) => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 12px',
    border: `1px solid ${errors[field] ? '#dc2626' : '#d1d5db'}`,
    borderRadius: '6px',
    fontSize: '14px'
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          Hotel Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="e.g., Marriott Downtown"
          style={getFieldBorderStyle('name')}
        />
        {renderFieldError('name')}
      </div>

      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          Address *
        </label>
        <input
          type="text"
          value={formData.address}
          onChange={(e) => onChange('address', e.target.value)}
          placeholder="e.g., 123 Main St, City, State 12345"
          style={getFieldBorderStyle('address')}
        />
        {renderFieldError('address')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Check-in Date *
          </label>
          <input
            type="date"
            value={formData.checkInDate}
            onChange={(e) => onChange('checkInDate', e.target.value)}
            style={getFieldBorderStyle('checkInDate')}
          />
          {renderFieldError('checkInDate')}
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            Check-out Date *
          </label>
          <input
            type="date"
            value={formData.checkOutDate}
            onChange={(e) => onChange('checkOutDate', e.target.value)}
            style={getFieldBorderStyle('checkOutDate')}
          />
          {renderFieldError('checkOutDate')}
        </div>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
          Confirmation Number
        </label>
        <input
          type="text"
          value={formData.confirmationNumber}
          onChange={(e) => onChange('confirmationNumber', e.target.value)}
          placeholder="e.g., 123456789"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px'
          }}
        />
      </div>
    </div>
  );
});

export default HotelFields;
